/**
 * Whether a QuickBooks receipt still waiting in the receipts list names a
 * customer — issue #899, ADR 0273.
 *
 * A receipt Kelsey accepted without tagging it to a project lands in the
 * receipts list with no customer on it at all. What it does have is the note
 * she or the crew typed, and on the real ones that note names the customer:
 * "Brian Garrett/ chimney siding", "Land field/clint Johnson", "For cat
 * Edward's 1700 job". Closing a job out while one of those is still waiting
 * would close it with a cost missing, so the close-out pop-up warns about it.
 *
 * The match is deliberately plain, because a warning never blocks Send: every
 * word of the customer's first and last name must appear as a word in the
 * receipt's note or vendor, in any case, with apostrophes dropped so
 * "Edward's" reads as "Edwards". A customer without both a first and a last
 * name is matched on the company's name instead, or not at all. A surname
 * alone is never enough — "Smith" would name half the receipts there are.
 */

export interface ReceiptCustomer {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
}

export interface WaitingReceipt {
  vendor: string | null;
  description: string | null;
}

/** Lower case, apostrophes dropped, everything else not a letter or digit a gap. */
function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/['‘’`]/g, "")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/** The words a receipt has to carry to name this customer. Empty: no match. */
function nameWords(customer: ReceiptCustomer): string[] {
  const first = words(customer.first_name ?? "");
  const last = words(customer.last_name ?? "");
  if (first.length > 0 && last.length > 0) return [...first, ...last];
  return words(customer.company ?? "");
}

export function receiptNamesCustomer(
  receipt: WaitingReceipt,
  customer: ReceiptCustomer | null | undefined,
): boolean {
  if (!customer) return false;
  const wanted = nameWords(customer);
  if (wanted.length === 0) return false;
  const said = new Set(
    words(`${receipt.description ?? ""} ${receipt.vendor ?? ""}`),
  );
  return wanted.every((w) => said.has(w));
}
